import { ALL_PLEDGE_KEYS, DEFAULT_ENABLED_PLEDGE_KEYS } from './pledgesList.js';

/**
 * Per-classroom enabled-pledges storage.
 *
 * Mirrors the per-classroom enabledPages pattern in
 * `classroomPages/enabledStorage.ts`: localStorage is the temporary
 * source of truth at the UI layer until the backend per-classroom
 * settings column lands. Default is `DEFAULT_ENABLED_PLEDGE_KEYS`
 * (just the Pledge of Allegiance); teachers opt in to the others
 * from the classroom's Pledges settings tab.
 */

const STORAGE_KEY = (classroomId: string) => `classy:enabledPledges:${classroomId}`;

function safeLocal(): globalThis.Storage | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    return localStorage;
  } catch {
    return null;
  }
}

export function getEnabledPledgeKeys(classroomId: string): string[] {
  const store = safeLocal();
  if (!store) return [...DEFAULT_ENABLED_PLEDGE_KEYS];
  try {
    const raw = store.getItem(STORAGE_KEY(classroomId));
    if (!raw) return [...DEFAULT_ENABLED_PLEDGE_KEYS];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [...DEFAULT_ENABLED_PLEDGE_KEYS];
    const valid = parsed.filter((k): k is string => ALL_PLEDGE_KEYS.includes(k));
    // Always keep the registry order so reads come back in the same
    // sequence regardless of which order the teacher toggled.
    return ALL_PLEDGE_KEYS.filter((k) => valid.includes(k));
  } catch {
    return [...DEFAULT_ENABLED_PLEDGE_KEYS];
  }
}

export function setEnabledPledgeKeys(classroomId: string, keys: readonly string[]): void {
  const store = safeLocal();
  if (!store) return;
  const ordered = ALL_PLEDGE_KEYS.filter((k) => keys.includes(k));
  try {
    store.setItem(STORAGE_KEY(classroomId), JSON.stringify(ordered));
  } catch {
    /* ignore quota / private-mode errors */
  }
}
